import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  ClassChangeRequest,
  ClassChangeRequestStatus,
  OrgClassChangeRequestsApi,
} from '@/services/class-change-requests.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Select, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';

const STATUS_TONE = { pending: 'amber', approved: 'green', rejected: 'red', partial_failure: 'red' } as const;
const STATUS_LABEL = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', partial_failure: 'Needs attention' } as const;

/**
 * Cross-school Class Change Request — org tier. Approve runs entry
 * (admit in the target school) THEN exit (retire in the source school) —
 * see ClassChangeRequestsService.approve()'s doc comment. A 'Needs
 * attention' (partial_failure) row means entry succeeded but exit
 * failed — the student exists in BOTH schools until a human resolves it
 * manually; it is never re-approvable from here.
 */
export function OrgClassChangeRequestsPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<ClassChangeRequestStatus>('pending');
  const [actioning, setActioning] = useState<{ id: string; kind: 'approve' | 'reject' } | null>(null);
  const [remarksDraft, setRemarksDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  // ── New Request form state ──
  const [currentSchoolId, setCurrentSchoolId] = useState('');
  const [currentClassId, setCurrentClassId] = useState('');
  const [currentSectionId, setCurrentSectionId] = useState('');
  const [studentId, setStudentId] = useState('');
  const [newSchoolId, setNewSchoolId] = useState('');
  const [newClassId, setNewClassId] = useState('');
  const [newSectionId, setNewSectionId] = useState('');
  const [reason, setReason] = useState('');

  const { data: schools = [] } = useQuery({
    queryKey: ['org-class-change-schools'],
    queryFn: OrgClassChangeRequestsApi.schools,
  });

  const { data: currentClasses = [] } = useQuery({
    queryKey: ['org-class-change-classes', currentSchoolId],
    queryFn: () => OrgClassChangeRequestsApi.classesForSchool(currentSchoolId),
    enabled: !!currentSchoolId,
  });
  const currentSections = currentClasses.find((c) => c.id === currentClassId)?.sections ?? [];

  const { data: newClasses = [] } = useQuery({
    queryKey: ['org-class-change-classes', newSchoolId],
    queryFn: () => OrgClassChangeRequestsApi.classesForSchool(newSchoolId),
    enabled: !!newSchoolId,
  });
  const newSections = newClasses.find((c) => c.id === newClassId)?.sections ?? [];

  const { data: students = [] } = useQuery({
    queryKey: ['org-class-change-students', currentSchoolId, currentSectionId],
    queryFn: () => OrgClassChangeRequestsApi.studentsForSection(currentSchoolId, currentSectionId),
    enabled: !!currentSchoolId && !!currentSectionId,
  });

  useEffect(() => {
    setCurrentClassId('');
    setCurrentSectionId('');
    setStudentId('');
  }, [currentSchoolId]);
  useEffect(() => {
    setCurrentSectionId('');
    setStudentId('');
  }, [currentClassId]);
  useEffect(() => {
    setStudentId('');
  }, [currentSectionId]);
  useEffect(() => {
    setNewClassId('');
    setNewSectionId('');
  }, [newSchoolId]);
  useEffect(() => {
    setNewSectionId('');
  }, [newClassId]);

  const resetForm = () => {
    setCurrentSchoolId('');
    setCurrentClassId('');
    setCurrentSectionId('');
    setStudentId('');
    setNewSchoolId('');
    setNewClassId('');
    setNewSectionId('');
    setReason('');
  };

  const create = useMutation({
    mutationFn: () =>
      OrgClassChangeRequestsApi.create({
        currentSchoolId,
        studentId,
        newSchoolId,
        newSectionId,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-class-change-requests'] });
      resetForm();
    },
    onError: (e: any) => setError(e?.response?.data?.error?.message ?? 'Failed to submit request'),
  });

  const createReady = !!currentSchoolId && !!studentId && !!newSchoolId && !!newSectionId;

  const { data: requests = [], isFetching } = useQuery({
    queryKey: ['org-class-change-requests', status],
    queryFn: () => OrgClassChangeRequestsApi.list({ status }),
  });

  const approve = useMutation({
    mutationFn: ({ id, remarks }: { id: string; remarks?: string }) => OrgClassChangeRequestsApi.approve(id, remarks),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-class-change-requests'] });
      setActioning(null);
      setRemarksDraft('');
    },
    onError: (e: any) => setError(e?.response?.data?.error?.message ?? 'Approval failed'),
  });

  const reject = useMutation({
    mutationFn: ({ id, remarks }: { id: string; remarks?: string }) => OrgClassChangeRequestsApi.reject(id, remarks),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-class-change-requests'] });
      setActioning(null);
      setRemarksDraft('');
    },
    onError: (e: any) => setError(e?.response?.data?.error?.message ?? 'Rejection failed'),
  });

  const busy = approve.isPending || reject.isPending;

  return (
    <div>
      <PageHeader
        title="Class Change Requests"
        description="Cross-school class change requests — approving admits the student at the target school, then retires them at the source school."
      />

      {error && (
        <div className="mb-4 flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            &times;
          </button>
        </div>
      )}

      <div className="mb-6 space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700">New Request</h3>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="School" required>
            <Select value={currentSchoolId} onChange={(e) => setCurrentSchoolId(e.target.value)}>
              <option value="">Select</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Current Class" required>
            <Select value={currentClassId} onChange={(e) => setCurrentClassId(e.target.value)} disabled={!currentSchoolId}>
              <option value="">Select</option>
              {currentClasses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Current Section" required>
            <Select value={currentSectionId} onChange={(e) => setCurrentSectionId(e.target.value)} disabled={!currentClassId}>
              <option value="">Select</option>
              {currentSections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Student" required>
            <Select value={studentId} onChange={(e) => setStudentId(e.target.value)} disabled={!currentSectionId}>
              <option value="">Select</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.admissionNo})
                </option>
              ))}
            </Select>
          </Field>
          <Field label="New School" required>
            <Select value={newSchoolId} onChange={(e) => setNewSchoolId(e.target.value)}>
              <option value="">Select</option>
              {schools
                .filter((s) => s.id !== currentSchoolId)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label="New Class" required>
            <Select value={newClassId} onChange={(e) => setNewClassId(e.target.value)} disabled={!newSchoolId}>
              <option value="">Select</option>
              {newClasses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="New Section" required>
            <Select value={newSectionId} onChange={(e) => setNewSectionId(e.target.value)} disabled={!newClassId}>
              <option value="">Select</option>
              {newSections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Reason">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
        </Field>

        <div className="flex justify-end">
          <button
            type="button"
            className="btn-primary"
            disabled={!createReady || create.isPending}
            onClick={() => {
              setError(null);
              create.mutate();
            }}
          >
            {create.isPending ? <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> : null}
            Submit Request
          </button>
        </div>
      </div>

      <div className="mb-4">
        <Select className="!w-48" value={status} onChange={(e) => setStatus(e.target.value as ClassChangeRequestStatus)}>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="partial_failure">Needs attention</option>
        </Select>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Student</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">From</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">To</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Reason</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isFetching ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-400">
                  Loading…
                </td>
              </tr>
            ) : requests.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-400">
                  No requests.
                </td>
              </tr>
            ) : (
              requests.map((r: ClassChangeRequest) => (
                <>
                  <tr key={r.id}>
                    <td className="px-4 py-2.5 text-sm text-slate-800">
                      {r.studentName} <span className="text-slate-400">({r.admissionNo})</span>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-slate-600">
                      {r.currentClassName} / {r.currentSectionName}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-slate-600">
                      {r.newClassName} / {r.newSectionName}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-slate-500">{r.reason || '—'}</td>
                    <td className="px-4 py-2.5">
                      <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {r.status === 'pending' && (
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            className="text-sm text-emerald-600 hover:text-emerald-800"
                            onClick={() => {
                              setError(null);
                              setRemarksDraft('');
                              setActioning({ id: r.id, kind: 'approve' });
                            }}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="text-sm text-red-500 hover:text-red-700"
                            onClick={() => {
                              setError(null);
                              setRemarksDraft('');
                              setActioning({ id: r.id, kind: 'reject' });
                            }}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {r.status === 'partial_failure' && r.partialFailureNote && (
                    <tr key={`${r.id}-note`}>
                      <td colSpan={6} className="bg-red-50 px-4 py-2 text-sm text-red-700">
                        <AlertTriangle className="mr-1.5 inline h-4 w-4" />
                        {r.partialFailureNote}
                      </td>
                    </tr>
                  )}
                  {actioning?.id === r.id && (
                    <tr key={`${r.id}-action`}>
                      <td colSpan={6} className="border-t border-slate-100 bg-slate-50 px-4 py-3">
                        <div className="flex items-start gap-3">
                          <Textarea
                            className="flex-1"
                            rows={2}
                            placeholder="Remarks (optional)"
                            value={remarksDraft}
                            onChange={(e) => setRemarksDraft(e.target.value)}
                          />
                          <div className="flex flex-col gap-2">
                            <button
                              type="button"
                              className={actioning.kind === 'approve' ? 'btn-primary' : 'btn-secondary bg-red-600 text-white hover:bg-red-700'}
                              disabled={busy}
                              onClick={() =>
                                actioning.kind === 'approve'
                                  ? approve.mutate({ id: r.id, remarks: remarksDraft.trim() || undefined })
                                  : reject.mutate({ id: r.id, remarks: remarksDraft.trim() || undefined })
                              }
                            >
                              {busy ? <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> : null}
                              Confirm {actioning.kind === 'approve' ? 'Approve' : 'Reject'}
                            </button>
                            <button type="button" className="text-sm text-slate-500 hover:text-slate-700" onClick={() => setActioning(null)}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
